/**
 * RegistrationFormContent - Reusable form for account type selection and club/player setup
 * Used by RegisterScreen (email signup) and CompleteProfileScreen (OAuth new user)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ScrollView,
  Modal,
  FlatList,
  Image,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { searchClaimableHotspots, getClaimableHotspots, CLAIMABLE_CATEGORIES } from '../services/database';
import { supabase } from '../services/supabase';

const CATEGORY_LABELS = { CLUB: 'Club', BAR: 'Bar', CAFE: 'Café', UNIVERSITY: 'University', LIBRARY: 'Library', OTHER: 'Other' };
const CLAIM_TAG_OPTIONS = ['coffee-shop', 'bar', 'library', 'casual', 'tournament', 'beginner-friendly', 'indoor', 'outdoor'];
const MAX_EVIDENCE_IMAGES = 5;

// Normalize handle to full URL — accepts "handle", "@handle", or full URL
const toInstagramUrl = (v) => {
  if (!v?.trim()) return null;
  const s = v.trim();
  if (/^https?:\/\//i.test(s)) return s;
  const handle = s.replace(/^@/, '').replace(/.*instagram\.com\/([^/?]+).*/, '$1').split('/')[0];
  return handle ? `https://instagram.com/${handle}` : null;
};
const toTikTokUrl = (v) => {
  if (!v?.trim()) return null;
  const s = v.trim();
  if (/^https?:\/\//i.test(s)) return s;
  let handle = s.replace(/^@/, '').replace(/.*tiktok\.com\/@?([^/?]+).*/, '$1').split('/')[0];
  if (handle && !handle.startsWith('@')) handle = `@${handle}`;
  return handle ? `https://tiktok.com/${handle}` : null;
};

export default function RegistrationFormContent({
  isOAuth = false,
  suggestedUsername = '',
  displayEmail = '',
  onSubmit,
  loading = false,
  errorMessage = '',
  onClearError,
  theme = {},
  showEmailPassword = true,
  showSocialNote = true,
  submitLabel = 'Create Account',
  headerTagline = 'Create your account',
}) {
  const [username, setUsername] = useState(suggestedUsername || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountType, setAccountType] = useState('PLAYER');
  const [chesscomUsername, setChesscomUsername] = useState('');
  const [lichessUsername, setLichessUsername] = useState('');
  const [clubName, setClubName] = useState('');
  const [clubAddress, setClubAddress] = useState('');
  const [submissionPitch, setSubmissionPitch] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [clubMode, setClubMode] = useState('NEW');
  const [showClubSearchModal, setShowClubSearchModal] = useState(false);
  const [clubSearchQuery, setClubSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchingClubs, setSearchingClubs] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [claimProofDescription, setClaimProofDescription] = useState('');
  const [claimContactInfo, setClaimContactInfo] = useState('');
  const [claimSearchCity, setClaimSearchCity] = useState('Toronto');
  const [claimSearchCategories, setClaimSearchCategories] = useState([]);
  const [claimSearchTags, setClaimSearchTags] = useState([]);
  const [showClaimFilters, setShowClaimFilters] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Profile setup fields
  const [profileImage, setProfileImage] = useState(null);   // { uri, ... } from ImagePicker
  const [city, setCity] = useState('');
  const [bio, setBio] = useState('');

  // Step-based navigation
  const [currentStep, setCurrentStep] = useState(1);  // 1=Account, 2=Details, 3=Profile
  const [localError, setLocalError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Evidence images for club ownership proof
  const [evidenceImages, setEvidenceImages] = useState([]);  // [{ uri, ... }, ...]

  useEffect(() => { setUsername(suggestedUsername || ''); }, [suggestedUsername]);
  useEffect(() => { setEmail(displayEmail || ''); }, [displayEmail]);

  // ─── Web file picker helper (more reliable than expo-image-picker on web) ───
  const pickFileWeb = (accept = 'image/*', multiple = false) => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.multiple = multiple;
      input.onchange = async () => {
        const files = Array.from(input.files || []);
        if (files.length === 0) return resolve([]);
        const assets = await Promise.all(files.map(async (file) => {
          const uri = URL.createObjectURL(file);
          // Read as ArrayBuffer for direct Supabase upload
          const arrayBuffer = await file.arrayBuffer();
          return { uri, file, arrayBuffer, width: 0, height: 0 };
        }));
        resolve(assets);
      };
      input.click();
    });
  };

  // ─── Image Pickers ───────────────────────────────────────
  const pickProfileImage = async () => {
    try {
      if (Platform.OS === 'web') {
        const assets = await pickFileWeb('image/*', false);
        if (assets.length > 0) {
          console.log('✅ Web profile image picked:', assets[0].uri?.substring(0, 60));
          setProfileImage(assets[0]);
        }
      } else {
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsEditing: true,
          aspect: [1, 1],
          quality: 0.8,
          base64: true,
        });
        if (!result.canceled && result.assets?.[0]) {
          console.log('✅ Native profile image picked');
          setProfileImage(result.assets[0]);
        }
      }
    } catch (e) {
      console.error('Error picking profile image:', e);
    }
  };

  const pickEvidenceImages = async () => {
    try {
      if (Platform.OS === 'web') {
        const assets = await pickFileWeb('image/*', true);
        if (assets.length > 0) {
          setEvidenceImages(prev => [...prev, ...assets].slice(0, MAX_EVIDENCE_IMAGES));
        }
      } else {
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsMultipleSelection: true,
          quality: 0.8,
          base64: true,
        });
        if (!result.canceled && result.assets) {
          setEvidenceImages(prev => [...prev, ...result.assets].slice(0, MAX_EVIDENCE_IMAGES));
        }
      }
    } catch (e) {
      console.error('Error picking evidence images:', e);
    }
  };

  const removeEvidenceImage = (index) => {
    setEvidenceImages(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Club Search ─────────────────────────────────────────
  const toggleClaimCategory = (cat) => {
    setClaimSearchCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };
  const toggleClaimTag = (tag) => {
    setClaimSearchTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  const getClaimSearchOptions = () => ({
    limit: 100,
    city: claimSearchCity.trim() || null,
    categories: claimSearchCategories.length > 0 ? claimSearchCategories : null,
    tags: claimSearchTags.length > 0 ? claimSearchTags : null,
  });

  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      const opts = getClaimSearchOptions();
      if (!query || query.trim().length < 2) {
        try {
          const clubs = await getClaimableHotspots(opts);
          setSearchResults(clubs);
        } catch (e) { console.error(e); }
      } else {
        try {
          const results = await searchClaimableHotspots(query, opts);
          setSearchResults(results);
        } catch (e) { setSearchResults([]); }
        finally { setSearchingClubs(false); }
        return;
      }
      setSearchingClubs(false);
    }, 300);
  }, [claimSearchCity, claimSearchCategories, claimSearchTags]);

  useEffect(() => {
    if (showClubSearchModal) { setSearchingClubs(true); debouncedSearch(clubSearchQuery); }
  }, [showClubSearchModal]);
  useEffect(() => {
    if (showClubSearchModal) { setSearchingClubs(true); debouncedSearch(clubSearchQuery); }
  }, [clubSearchQuery, claimSearchCity, claimSearchCategories, claimSearchTags, showClubSearchModal, debouncedSearch]);
  useEffect(() => () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); }, []);

  const handleSelectClub = (club) => { setSelectedClub(club); setShowClubSearchModal(false); };
  const handleClearSelectedClub = () => { setSelectedClub(null); setClaimProofDescription(''); setClaimContactInfo(''); };

  // ─── Step Validation ─────────────────────────────────────
  const validateStep1 = () => {
    if (!username || username.trim().length < 3) return 'Username must be at least 3 characters';
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Username can only contain letters, numbers, and underscores';
    if (showEmailPassword) {
      if (!email?.trim()) return 'Please enter your email';
      if (!password) return 'Please enter a password';
      if (password.length < 8) return 'Password must be at least 8 characters';
      if (password !== confirmPassword) return 'Passwords do not match';
    }
    return null;
  };

  const validateStep2 = () => {
    if (accountType === 'CLUB') {
      if (clubMode === 'CLAIM') {
        if (!selectedClub) return 'Please select a spot to claim';
        if (!claimProofDescription.trim()) return 'Please describe how you can prove ownership';
        if (claimProofDescription.trim().length < 30) return 'Please provide more detail (at least 30 characters)';
      } else {
        if (!clubName) return 'Please enter your club name';
        if (!clubAddress?.trim()) return 'Please enter your club address';
        if (!submissionPitch?.trim()) return 'Please tell us about your club';
        if (submissionPitch.trim().length < 50) return 'Club description must be at least 50 characters';
      }
    }
    // Player step 2 is all optional (chess accounts)
    return null;
  };

  const handleNextStep = async () => {
    if (checkingUsername) return;
    let err = null;

    if (currentStep === 1) {
      err = validateStep1();

      // If username passes basic validation, also ensure it's available before proceeding.
      if (!err && username?.trim()) {
        try {
          setCheckingUsername(true);
          const normalized = username.trim().toLowerCase();
          const { data, error } = await supabase
            .from('users')
            .select('id')
            .ilike('username', normalized)
            .limit(1);

          if (error) {
            console.error('Error checking username availability:', error);
            err = 'Unable to check username right now. Please try again.';
          } else if (Array.isArray(data) && data.length > 0) {
            err = 'This username is already taken. Please choose another.';
          }
        } catch (e) {
          console.error('Unexpected error checking username availability:', e);
          err = 'Unable to check username right now. Please try again.';
        } finally {
          setCheckingUsername(false);
        }
      }
    }

    if (currentStep === 2) {
      err = validateStep2();
    }

    if (err) {
      setLocalError(err);
      onClearError?.();
      return;
    }

    setLocalError('');
    onClearError?.();
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setLocalError('');
    onClearError?.();
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    // Final validation covers all steps
    const err1 = validateStep1();
    if (err1) { setLocalError(err1); setCurrentStep(1); return; }
    const err2 = validateStep2();
    if (err2) { setLocalError(err2); setCurrentStep(2); return; }
    setLocalError('');
    onClearError?.();
    onSubmit?.({
      username: username.trim().toLowerCase(),
      email: email?.trim(),
      password: showEmailPassword ? password : undefined,
      accountType,
      clubMode,
      clubName: clubName?.trim(),
      clubAddress: clubAddress?.trim(),
      submissionPitch: submissionPitch?.trim(),
      websiteUrl: websiteUrl?.trim() || null,
      instagramUrl: toInstagramUrl(instagramUrl) || instagramUrl?.trim() || null,
      twitterUrl: twitterUrl?.trim() || null,
      facebookUrl: facebookUrl?.trim() || null,
      tiktokUrl: toTikTokUrl(tiktokUrl) || tiktokUrl?.trim() || null,
      selectedClub,
      claimProofDescription: claimProofDescription?.trim(),
      claimContactInfo: claimContactInfo?.trim() || null,
      chesscomUsername: chesscomUsername?.trim() || null,
      lichessUsername: lichessUsername?.trim() || null,
      // Profile setup fields
      profileImage,              // raw image asset (will be uploaded by handler)
      evidenceImages,            // raw image assets for club evidence
      city: city?.trim() || null,
      bio: bio?.trim() || null,
    });
  };

  const t = { ...defaultTheme, ...theme };

  // ─── Social Links Section (shared between CLUB NEW and profile setup) ───
  const renderSocialLinks = () => (
    <View style={styles.socialLinksSection}>
      <Text style={[styles.sectionLabel, { color: t.text }]}>Social Links</Text>
      <Text style={[styles.sectionHint, { color: t.textSecondary }]}>Optional — help people find you online.</Text>
      <View style={styles.socialLinkRow}>
        <Icon name="language" size={18} color={t.textSecondary} style={styles.socialIcon} />
        <TextInput style={[styles.input, styles.socialInput, { color: t.text, borderBottomColor: t.border }]} placeholder="Website URL" placeholderTextColor={t.textSecondary} value={websiteUrl} onChangeText={setWebsiteUrl} keyboardType="url" autoCapitalize="none" />
      </View>
      <View style={styles.socialLinkRow}>
        <Text style={[styles.socialIconText, { color: t.textSecondary }]}>IG</Text>
        <TextInput style={[styles.input, styles.socialInput, { color: t.text, borderBottomColor: t.border }]} placeholder="Instagram handle (e.g. chessmaps)" placeholderTextColor={t.textSecondary} value={instagramUrl} onChangeText={setInstagramUrl} autoCapitalize="none" />
      </View>
      <View style={styles.socialLinkRow}>
        <Text style={[styles.socialIconText, { color: t.textSecondary }]}>TT</Text>
        <TextInput style={[styles.input, styles.socialInput, { color: t.text, borderBottomColor: t.border }]} placeholder="TikTok handle (e.g. @chessmaps)" placeholderTextColor={t.textSecondary} value={tiktokUrl} onChangeText={setTiktokUrl} autoCapitalize="none" />
      </View>
      <View style={styles.socialLinkRow}>
        <Text style={[styles.socialIconText, { color: t.textSecondary }]}>X</Text>
        <TextInput style={[styles.input, styles.socialInput, { color: t.text, borderBottomColor: t.border }]} placeholder="Twitter / X URL" placeholderTextColor={t.textSecondary} value={twitterUrl} onChangeText={setTwitterUrl} keyboardType="url" autoCapitalize="none" />
      </View>
      <View style={styles.socialLinkRow}>
        <Text style={[styles.socialIconText, { color: t.textSecondary }]}>FB</Text>
        <TextInput style={[styles.input, styles.socialInput, { color: t.text, borderBottomColor: t.border }]} placeholder="Facebook URL" placeholderTextColor={t.textSecondary} value={facebookUrl} onChangeText={setFacebookUrl} keyboardType="url" autoCapitalize="none" />
      </View>
    </View>
  );

  // ─── Step titles & descriptions ─────────────────────────
  const stepMeta = {
    1: { title: 'Account Setup', subtitle: 'Choose your account type and set up your credentials.' },
    2: {
      title: accountType === 'CLUB' ? 'Club Details' : 'Chess Accounts',
      subtitle: accountType === 'CLUB'
        ? 'Tell us about your club so we can review your application.'
        : 'Link your chess accounts to import ratings. (Optional)',
    },
    3: { title: 'Profile Setup', subtitle: 'Personalize your profile — you can always update later.' },
  };

  const displayError = localError || errorMessage;

  return (
    <>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} showsHorizontalScrollIndicator={false} horizontal={false} keyboardShouldPersistTaps="handled" bounces={false} overScrollMode="never">

        {/* ─── Step Indicator ─────────────────────────── */}
        <View style={styles.stepIndicator}>
          {[1, 2, 3].map((step) => (
            <View key={step} style={[styles.stepDot, { backgroundColor: currentStep >= step ? t.accent : t.border }]} />
          ))}
        </View>

        {/* ─── Step Header ────────────────────────────── */}
        <View style={styles.stepHeader}>
          <Text style={[styles.stepTitle, { color: t.text }]}>{stepMeta[currentStep].title}</Text>
          <Text style={[styles.stepSubtitle, { color: t.textSecondary }]}>{stepMeta[currentStep].subtitle}</Text>
        </View>

        {/* ═══════════════════════════════════════════════
            STEP 1 — Account Type, Username, Credentials
           ═══════════════════════════════════════════════ */}
        {currentStep === 1 && (
          <>
            {showSocialNote && !isOAuth && (
              <View style={[styles.socialNote, { backgroundColor: t.inputBg, borderColor: t.border }]}>
                <Text style={[styles.socialNoteText, { color: t.textSecondary }]}>
                  Want to use Google or Apple? Sign in from the Login screen.
                </Text>
              </View>
            )}

            {isOAuth && (
              <View style={[styles.oauthBanner, { backgroundColor: t.inputBg, borderColor: t.border }]}>
                <Icon name="check-circle" size={24} color={t.accent} />
                <Text style={[styles.oauthBannerText, { color: t.text }]}>
                  Welcome! Choose your account type and complete your profile.
                </Text>
              </View>
            )}

            <View style={styles.typeSelector}>
              <Pressable
                style={[styles.typeButton, { backgroundColor: accountType === 'PLAYER' ? t.accent : t.inputBg, borderColor: accountType === 'PLAYER' ? t.accent : t.border }]}
                onPress={() => setAccountType('PLAYER')}
              >
                <Text style={[styles.typeButtonText, { color: accountType === 'PLAYER' ? '#0A0A0A' : t.textSecondary }]}>♟️ Player</Text>
              </Pressable>
              <Pressable
                style={[styles.typeButton, { backgroundColor: accountType === 'CLUB' ? t.accent : t.inputBg, borderColor: accountType === 'CLUB' ? t.accent : t.border }]}
                onPress={() => setAccountType('CLUB')}
              >
                <Text style={[styles.typeButtonText, { color: accountType === 'CLUB' ? '#0A0A0A' : t.textSecondary }]}>🏛️ Club</Text>
              </Pressable>
            </View>

            <View style={styles.inputGroup}>
              <TextInput
                style={[styles.input, { color: t.text, borderBottomColor: t.border }]}
                placeholder="Username *"
                placeholderTextColor={t.textSecondary}
                value={username}
                onChangeText={(v) => { setUsername(v); setLocalError(''); onClearError?.(); }}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {showEmailPassword && (
                <>
                  <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Email *" placeholderTextColor={t.textSecondary} value={email} onChangeText={(v) => { setEmail(v); setLocalError(''); onClearError?.(); }} autoCapitalize="none" keyboardType="email-address" />
                  <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Password (min 8 characters) *" placeholderTextColor={t.textSecondary} value={password} onChangeText={(v) => { setPassword(v); setLocalError(''); onClearError?.(); }} secureTextEntry autoCapitalize="none" />
                  <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Confirm Password *" placeholderTextColor={t.textSecondary} value={confirmPassword} onChangeText={(v) => { setConfirmPassword(v); setLocalError(''); onClearError?.(); }} secureTextEntry autoCapitalize="none" />
                </>
              )}
            </View>
          </>
        )}

        {/* ═══════════════════════════════════════════════
            STEP 2 — Account-specific Details
           ═══════════════════════════════════════════════ */}
        {currentStep === 2 && (
          <View style={styles.inputGroup}>
            {accountType === 'CLUB' && (
              <>
                <View style={styles.clubModeContainer}>
                  <Text style={[styles.clubModeLabel, { color: t.text }]}>What would you like to do?</Text>
                  <View style={styles.clubModeToggle}>
                    <Pressable
                      style={[styles.clubModeButton, { backgroundColor: clubMode === 'NEW' ? t.accent : t.inputBg, borderColor: clubMode === 'NEW' ? t.accent : t.border }]}
                      onPress={() => { setClubMode('NEW'); setSelectedClub(null); }}
                    >
                      <Icon name="add-business" size={18} color={clubMode === 'NEW' ? '#0A0A0A' : t.textSecondary} />
                      <Text style={[styles.clubModeButtonText, { color: clubMode === 'NEW' ? '#0A0A0A' : t.textSecondary }]}>Create New Club</Text>
                    </Pressable>
                    <Pressable
                      style={[styles.clubModeButton, { backgroundColor: clubMode === 'CLAIM' ? t.accent : t.inputBg, borderColor: clubMode === 'CLAIM' ? t.accent : t.border }]}
                      onPress={() => setClubMode('CLAIM')}
                    >
                      <Icon name="verified" size={18} color={clubMode === 'CLAIM' ? '#0A0A0A' : t.textSecondary} />
                      <Text style={[styles.clubModeButtonText, { color: clubMode === 'CLAIM' ? '#0A0A0A' : t.textSecondary }]}>Claim Existing Club</Text>
                    </Pressable>
                  </View>
                </View>

                {clubMode === 'CLAIM' && (
                  <>
                    {selectedClub ? (
                      <View style={[styles.selectedClubCard, { backgroundColor: t.inputBg, borderColor: t.accent }]}>
                        <View style={styles.selectedClubHeader}>
                          <Icon name="business" size={24} color={t.accent} />
                          <View style={styles.selectedClubInfo}>
                            <Text style={[styles.selectedClubName, { color: t.text }]}>{selectedClub.name}</Text>
                            {selectedClub.address && <Text style={[styles.selectedClubAddress, { color: t.textSecondary }]}>{selectedClub.address}</Text>}
                          </View>
                          <TouchableOpacity onPress={handleClearSelectedClub}><Icon name="close" size={20} color={t.textSecondary} /></TouchableOpacity>
                        </View>
                      </View>
                    ) : (
                      <TouchableOpacity style={[styles.searchClubButton, { backgroundColor: t.inputBg, borderColor: t.border }]} onPress={() => setShowClubSearchModal(true)}>
                        <Icon name="search" size={20} color={t.textSecondary} />
                        <Text style={[styles.searchClubButtonText, { color: t.textSecondary }]}>Search for your club or venue...</Text>
                      </TouchableOpacity>
                    )}
                    <TextInput
                      style={[styles.textArea, { color: t.text, backgroundColor: t.inputBg }]}
                      placeholder="How can you prove you own/manage this club? *"
                      placeholderTextColor={t.textSecondary}
                      value={claimProofDescription}
                      onChangeText={setClaimProofDescription}
                      multiline
                      numberOfLines={3}
                      maxLength={500}
                    />
                    <Text style={[styles.charCount, { color: t.textSecondary }]}>{claimProofDescription.length}/500</Text>
                    <TextInput
                      style={[styles.input, { color: t.text, borderBottomColor: t.border }]}
                      placeholder="Additional contact info (phone, club email)"
                      placeholderTextColor={t.textSecondary}
                      value={claimContactInfo}
                      onChangeText={setClaimContactInfo}
                    />

                    {/* Evidence Upload */}
                    <View style={styles.evidenceSection}>
                      <Text style={[styles.sectionLabel, { color: t.text }]}>Evidence of Ownership</Text>
                      <Text style={[styles.sectionHint, { color: t.textSecondary }]}>
                        Upload photos or documents proving you own or manage this venue (e.g. business license, lease, signage, social media screenshots). Up to {MAX_EVIDENCE_IMAGES} images.
                      </Text>
                      <View style={styles.evidenceGrid}>
                        {evidenceImages.map((img, i) => (
                          <View key={i} style={styles.evidenceThumbWrap}>
                            <Image source={{ uri: img.uri }} style={styles.evidenceThumb} />
                            <TouchableOpacity style={styles.evidenceRemoveBtn} onPress={() => removeEvidenceImage(i)}>
                              <Icon name="close" size={14} color="#fff" />
                            </TouchableOpacity>
                          </View>
                        ))}
                        {evidenceImages.length < MAX_EVIDENCE_IMAGES && (
                          <TouchableOpacity style={[styles.evidenceAddBtn, { backgroundColor: t.inputBg, borderColor: t.border }]} onPress={pickEvidenceImages}>
                            <Icon name="add-a-photo" size={24} color={t.textSecondary} />
                            <Text style={[styles.evidenceAddText, { color: t.textSecondary }]}>Add</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  </>
                )}

                {clubMode === 'NEW' && (
                  <>
                    <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Club Name *" placeholderTextColor={t.textSecondary} value={clubName} onChangeText={setClubName} />
                    <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Club Address *" placeholderTextColor={t.textSecondary} value={clubAddress} onChangeText={setClubAddress} />
                    <TextInput
                      style={[styles.textArea, { color: t.text, backgroundColor: t.inputBg }]}
                      placeholder="Tell us about your club * (min 50 characters)"
                      placeholderTextColor={t.textSecondary}
                      value={submissionPitch}
                      onChangeText={setSubmissionPitch}
                      multiline
                      numberOfLines={4}
                      maxLength={1000}
                    />
                    <Text style={[styles.charCount, { color: t.textSecondary }]}>{submissionPitch.length}/1000</Text>
                  </>
                )}

                {/* Club Approval Notice */}
                <View style={[styles.approvalNotice, { backgroundColor: '#FFF8E1', borderColor: '#FFD54F' }]}>
                  <Icon name="info-outline" size={18} color="#F57F17" />
                  <Text style={styles.approvalNoticeText}>
                    Club accounts require approval. We'll review your application and email you once approved.
                    {clubMode === 'CLAIM' ? ' Evidence documents help speed up the review.' : ''}
                    {' '}For approval inquiries or to submit your request directly, email chessmapsapp@gmail.com.
                  </Text>
                </View>
              </>
            )}

            {accountType === 'PLAYER' && (
              <>
                <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Chess.com username (optional)" placeholderTextColor={t.textSecondary} value={chesscomUsername} onChangeText={setChesscomUsername} autoCapitalize="none" />
                <TextInput style={[styles.input, { color: t.text, borderBottomColor: t.border }]} placeholder="Lichess username (optional)" placeholderTextColor={t.textSecondary} value={lichessUsername} onChangeText={setLichessUsername} autoCapitalize="none" />
              </>
            )}
          </View>
        )}

        {/* ═══════════════════════════════════════════════
            STEP 3 — Profile Setup (all accounts)
           ═══════════════════════════════════════════════ */}
        {currentStep === 3 && (
          <>
            {/* Profile Picture Picker */}
            <View style={styles.profilePicRow}>
              <TouchableOpacity onPress={pickProfileImage} style={styles.profilePicTouchable}>
                {profileImage ? (
                  <Image source={{ uri: profileImage.uri }} style={styles.profilePicPreview} />
                ) : (
                  <View style={[styles.profilePicPlaceholder, { backgroundColor: t.inputBg, borderColor: t.border }]}>
                    <Icon name="add-a-photo" size={28} color={t.textSecondary} />
                  </View>
                )}
              </TouchableOpacity>
              <View style={styles.profilePicTextWrap}>
                <Text style={[styles.profilePicLabel, { color: t.text }]}>
                  {profileImage ? 'Photo selected' : 'Add profile photo'}
                </Text>
                <TouchableOpacity onPress={pickProfileImage}>
                  <Text style={[styles.profilePicAction, { color: t.accent }]}>
                    {profileImage ? 'Change' : 'Choose'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputGroup}>
              <TextInput
                style={[styles.input, { color: t.text, borderBottomColor: t.border }]}
                placeholder="City (e.g. Toronto)"
                placeholderTextColor={t.textSecondary}
                value={city}
                onChangeText={setCity}
              />
              <TextInput
                style={[styles.textArea, { color: t.text, backgroundColor: t.inputBg, marginTop: 4 }]}
                placeholder="Short bio (optional)"
                placeholderTextColor={t.textSecondary}
                value={bio}
                onChangeText={setBio}
                multiline
                numberOfLines={3}
                maxLength={300}
              />
              {bio.length > 0 && <Text style={[styles.charCount, { color: t.textSecondary }]}>{bio.length}/300</Text>}
            </View>

            {/* Social links for all account types */}
            {renderSocialLinks()}
          </>
        )}

        {/* ─── Error Display ──────────────────────────── */}
        {displayError ? <View style={styles.errorContainer}><Text style={styles.errorText}>{displayError}</Text></View> : null}

        {/* ─── Navigation Buttons ─────────────────────── */}
        <View style={styles.stepNavContainer}>
          {currentStep > 1 && (
            <Pressable
              style={({ pressed }) => [styles.stepBackBtn, { borderColor: t.border }, pressed && styles.btnPressed]}
              onPress={handlePrevStep}
            >
              <Icon name="arrow-back" size={20} color={t.text} />
              <Text style={[styles.stepBackBtnText, { color: t.text }]}>Back</Text>
            </Pressable>
          )}

          {currentStep < 3 ? (
            <Pressable
              style={({ pressed }) => [
                styles.stepNextBtn,
                { backgroundColor: t.accent },
                currentStep === 1 && styles.stepBtnFull,
                pressed && styles.btnPressed,
              ]}
              onPress={handleNextStep}
              disabled={checkingUsername}
            >
              {currentStep === 1 && checkingUsername ? (
                <ActivityIndicator size="small" color="#0A0A0A" />
              ) : (
                <>
                  <Text style={styles.stepNextBtnText}>Next</Text>
                  <Icon name="arrow-forward" size={20} color="#0A0A0A" />
                </>
              )}
            </Pressable>
          ) : (
            <Pressable
              style={({ pressed }) => [
                styles.stepNextBtn,
                { backgroundColor: t.accent },
                pressed && styles.btnPressed,
                loading && styles.btnDisabled,
              ]}
              onPress={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#0A0A0A" />
              ) : (
                <>
                  <Text style={styles.stepNextBtnText}>{submitLabel}</Text>
                  <Icon name="check" size={20} color="#0A0A0A" />
                </>
              )}
            </Pressable>
          )}
        </View>

        {/* Skip link on optional steps */}
        {currentStep === 2 && accountType === 'PLAYER' && (
          <Pressable style={styles.skipLink} onPress={() => { setLocalError(''); setCurrentStep(3); }}>
            <Text style={[styles.skipLinkText, { color: t.textSecondary }]}>Skip this step</Text>
          </Pressable>
        )}
        {currentStep === 3 && (
          <Pressable style={styles.skipLink} onPress={handleSubmit} disabled={loading}>
            <Text style={[styles.skipLinkText, { color: t.textSecondary }]}>Skip and finish</Text>
          </Pressable>
        )}
      </ScrollView>

      {/* Club Search Modal */}
      {showClubSearchModal && (
        <Modal visible animationType="slide" transparent onRequestClose={() => setShowClubSearchModal(false)}>
          <View style={[styles.modalOverlay, Platform.OS === 'web' && styles.modalOverlayWeb]}>
            <View style={[styles.modalContent, { backgroundColor: t.card }]}>
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, { color: t.text }]}>Search for your club or venue</Text>
                <TouchableOpacity onPress={() => setShowClubSearchModal(false)}><Icon name="close" size={24} color={t.textSecondary} /></TouchableOpacity>
              </View>
              <View style={[styles.searchInputContainer, { backgroundColor: t.inputBg, borderColor: t.border }]}>
                <Icon name="search" size={20} color={t.textSecondary} />
                <TextInput style={[styles.searchInput, { color: t.text }]} placeholder="Search by name, city, or address..." placeholderTextColor={t.textSecondary} value={clubSearchQuery} onChangeText={setClubSearchQuery} autoFocus />
                {clubSearchQuery.length > 0 && <TouchableOpacity onPress={() => setClubSearchQuery('')}><Icon name="clear" size={20} color={t.textSecondary} /></TouchableOpacity>}
              </View>
              <TouchableOpacity style={[styles.claimFilterToggle, { borderColor: t.border }]} onPress={() => setShowClaimFilters(!showClaimFilters)}>
                <Icon name="filter-list" size={20} color={t.textSecondary} />
                <Text style={[styles.claimFilterToggleText, { color: t.textSecondary }]}>Filters</Text>
                <Icon name={showClaimFilters ? 'expand-less' : 'expand-more'} size={20} color={t.textSecondary} />
              </TouchableOpacity>
              {showClaimFilters && (
                <View style={[styles.claimFiltersPanel, { backgroundColor: t.inputBg, borderColor: t.border }]}>
                  <Text style={[styles.claimFilterLabel, { color: t.textSecondary }]}>City</Text>
                  <TextInput style={[styles.claimFilterInput, { color: t.text, borderColor: t.border }]} placeholder="e.g. Toronto" placeholderTextColor={t.textSecondary} value={claimSearchCity} onChangeText={setClaimSearchCity} />
                  <Text style={[styles.claimFilterLabel, { color: t.textSecondary, marginTop: 12 }]}>Type</Text>
                  <View style={styles.claimFilterChips}>
                    {CLAIMABLE_CATEGORIES.map((cat) => {
                      const sel = claimSearchCategories.length === 0 || claimSearchCategories.includes(cat);
                      return (
                        <TouchableOpacity key={cat} style={[styles.claimFilterChip, { backgroundColor: sel ? t.accent : t.card, borderColor: sel ? t.accent : t.border }]} onPress={() => toggleClaimCategory(cat)}>
                          <Text style={[styles.claimFilterChipText, { color: sel ? '#0A0A0A' : t.text }]}>{CATEGORY_LABELS[cat] || cat}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                  <Text style={[styles.claimFilterLabel, { color: t.textSecondary, marginTop: 12 }]}>Tags</Text>
                  <View style={styles.claimFilterChips}>
                    {CLAIM_TAG_OPTIONS.map((tag) => {
                      const sel = claimSearchTags.includes(tag);
                      return (
                        <TouchableOpacity key={tag} style={[styles.claimFilterChip, { backgroundColor: sel ? t.accent : t.card, borderColor: sel ? t.accent : t.border }]} onPress={() => toggleClaimTag(tag)}>
                          <Text style={[styles.claimFilterChipText, { color: sel ? '#0A0A0A' : t.text }]}>{tag.replace(/-/g, ' ')}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              )}
              {searchingClubs ? (
                <View style={styles.searchLoadingContainer}><ActivityIndicator size="large" color={t.accent} /><Text style={[styles.searchLoadingText, { color: t.textSecondary }]}>Searching...</Text></View>
              ) : searchResults.length === 0 ? (
                <View style={styles.searchEmptyContainer}>
                  <Icon name="business" size={48} color={t.textSecondary} />
                  <Text style={[styles.searchEmptyText, { color: t.textSecondary }]}>{clubSearchQuery ? 'No claimable spots found' : 'No claimable spots available'}</Text>
                </View>
              ) : (
                <FlatList
                  data={searchResults}
                  keyExtractor={(item) => item.id}
                  style={styles.searchResultsList}
                  renderItem={({ item }) => (
                    <TouchableOpacity style={[styles.clubResultItem, { borderColor: t.border }]} onPress={() => handleSelectClub(item)}>
                      <View style={styles.clubResultIcon}><Icon name="business" size={24} color={t.accent} /></View>
                      <View style={styles.clubResultInfo}>
                        <Text style={[styles.clubResultName, { color: t.text }]}>{item.name}</Text>
                        {item.address && <Text style={[styles.clubResultAddress, { color: t.textSecondary }]} numberOfLines={1}>📍 {item.address}</Text>}
                      </View>
                      <Icon name="chevron-right" size={20} color={t.textSecondary} />
                    </TouchableOpacity>
                  )}
                />
              )}
            </View>
          </View>
        </Modal>
      )}
    </>
  );
}

const defaultTheme = { bg: '#FAFAFA', card: '#FFFFFF', text: '#0A0A0A', textSecondary: '#666666', border: '#E5E5E5', inputBg: '#F5F5F5', accent: '#94EB98' };

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: 40 },
  // Step indicator
  stepIndicator: { flexDirection: 'row', justifyContent: 'center', gap: 8, marginBottom: 20 },
  stepDot: { width: 32, height: 6, borderRadius: 3 },
  stepHeader: { marginBottom: 20 },
  stepTitle: { fontSize: 24, fontWeight: '700', textAlign: 'center', marginBottom: 8 },
  stepSubtitle: { fontSize: 14, textAlign: 'center', lineHeight: 20 },
  // Step navigation buttons
  stepNavContainer: { flexDirection: 'row', gap: 12, marginTop: 24 },
  stepBackBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 52, borderRadius: 12, borderWidth: 2 },
  stepBackBtnText: { fontSize: 16, fontWeight: '600' },
  stepNextBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 52, borderRadius: 12 },
  stepNextBtnText: { color: '#0A0A0A', fontSize: 16, fontWeight: '600' },
  stepBtnFull: { flex: 1 },
  btnPressed: { opacity: 0.8, transform: [{ scale: 0.98 }] },
  btnDisabled: { opacity: 0.6 },
  skipLink: { alignItems: 'center', marginTop: 14, padding: 10 },
  skipLinkText: { fontSize: 14, fontWeight: '500' },
  tagline: { fontSize: 15, marginBottom: 16, textAlign: 'center' },
  socialNote: { padding: 12, borderRadius: 10, borderWidth: 1, marginBottom: 16 },
  socialNoteText: { fontSize: 14, textAlign: 'center' },
  oauthBanner: { flexDirection: 'row', alignItems: 'center', padding: 14, borderRadius: 10, borderWidth: 1, marginBottom: 16, gap: 12 },
  oauthBannerText: { flex: 1, fontSize: 14 },
  typeSelector: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  typeButton: { flex: 1, paddingVertical: 12, borderRadius: 10, alignItems: 'center', borderWidth: 1 },
  typeButtonText: { fontSize: 14, fontWeight: '500' },
  inputGroup: { gap: 20, marginBottom: 24 },
  input: { borderBottomWidth: 1, paddingVertical: 12, fontSize: 16, ...(Platform.OS === 'web' && { outlineStyle: 'none' }) },
  textArea: { borderRadius: 10, padding: 12, fontSize: 16, minHeight: 80, textAlignVertical: 'top' },
  charCount: { fontSize: 12, marginTop: -12 },
  clubModeContainer: { marginTop: 8 },
  clubModeLabel: { fontSize: 14, marginBottom: 10 },
  clubModeToggle: { flexDirection: 'row', gap: 12 },
  clubModeButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 10, borderWidth: 1 },
  clubModeButtonText: { fontSize: 13, fontWeight: '500' },
  selectedClubCard: { padding: 14, borderRadius: 10, borderWidth: 1 },
  selectedClubHeader: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  selectedClubInfo: { flex: 1 },
  selectedClubName: { fontSize: 15, fontWeight: '600' },
  selectedClubAddress: { fontSize: 13, marginTop: 2 },
  searchClubButton: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 10, borderWidth: 1 },
  searchClubButtonText: { fontSize: 14, color: '#666' },
  // Evidence section
  evidenceSection: { marginTop: 16 },
  evidenceGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 10 },
  evidenceThumbWrap: { position: 'relative', width: 80, height: 80 },
  evidenceThumb: { width: 80, height: 80, borderRadius: 10 },
  evidenceRemoveBtn: { position: 'absolute', top: -6, right: -6, backgroundColor: '#FF6B6B', borderRadius: 12, width: 22, height: 22, alignItems: 'center', justifyContent: 'center' },
  evidenceAddBtn: { width: 80, height: 80, borderRadius: 10, borderWidth: 1, borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center', gap: 4 },
  evidenceAddText: { fontSize: 11 },
  // Social links
  socialLinksSection: { marginTop: 16, gap: 4 },
  sectionLabel: { fontSize: 15, fontWeight: '600', marginBottom: 2 },
  sectionHint: { fontSize: 13, marginBottom: 8 },
  socialLinkRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  socialIcon: { width: 22, textAlign: 'center' },
  socialIconText: { fontSize: 13, fontWeight: '700', width: 22, textAlign: 'center' },
  socialInput: { flex: 1 },
  // Profile setup
  profileSetupSection: { marginBottom: 24, gap: 12 },
  profilePicRow: { flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 8 },
  profilePicTouchable: {},
  profilePicPreview: { width: 72, height: 72, borderRadius: 36 },
  profilePicPlaceholder: { width: 72, height: 72, borderRadius: 36, borderWidth: 1, borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center' },
  profilePicTextWrap: { flex: 1 },
  profilePicLabel: { fontSize: 15, fontWeight: '500' },
  profilePicAction: { fontSize: 14, fontWeight: '600', marginTop: 2 },
  // Approval notice
  approvalNotice: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 14, borderRadius: 10, borderWidth: 1, marginBottom: 16 },
  approvalNoticeText: { flex: 1, fontSize: 13, color: '#5D4037' },
  // Error / Submit
  errorContainer: { marginBottom: 16, padding: 12, backgroundColor: 'rgba(255,107,107,0.1)', borderRadius: 8 },
  errorText: { color: '#FF6B6B', fontSize: 14 },
  primaryButton: { backgroundColor: '#94EB98', paddingVertical: 16, borderRadius: 10, alignItems: 'center' },
  primaryButtonText: { color: '#0A0A0A', fontSize: 16, fontWeight: '600' },
  // Club search modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
  modalOverlayWeb: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000 },
  modalContent: { borderRadius: 24, paddingTop: 20, maxHeight: '80%', width: '90%', maxWidth: 500 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.1)' },
  modalTitle: { fontSize: 18, fontWeight: '600' },
  searchInputContainer: { flexDirection: 'row', alignItems: 'center', margin: 16, paddingHorizontal: 12, paddingVertical: 10, borderRadius: 10, gap: 10 },
  searchInput: { flex: 1, fontSize: 16, paddingVertical: 0 },
  claimFilterToggle: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 16, marginBottom: 12, padding: 12, borderRadius: 10, borderWidth: 1, gap: 8 },
  claimFilterToggleText: { flex: 1, fontSize: 14 },
  claimFiltersPanel: { marginHorizontal: 16, marginBottom: 16, padding: 16, borderRadius: 10, borderWidth: 1 },
  claimFilterLabel: { fontSize: 12, fontWeight: '600', marginBottom: 8 },
  claimFilterInput: { borderWidth: 1, borderRadius: 8, padding: 10, fontSize: 14 },
  claimFilterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  claimFilterChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, borderWidth: 1 },
  claimFilterChipText: { fontSize: 13, fontWeight: '500' },
  searchLoadingContainer: { alignItems: 'center', padding: 40, gap: 12 },
  searchLoadingText: { fontSize: 14 },
  searchEmptyContainer: { alignItems: 'center', padding: 40, gap: 12 },
  searchEmptyText: { fontSize: 16, textAlign: 'center' },
  searchResultsList: { maxHeight: 300 },
  clubResultItem: { flexDirection: 'row', alignItems: 'center', padding: 14, marginHorizontal: 16, marginBottom: 8, borderRadius: 10, borderWidth: 1, gap: 12 },
  clubResultIcon: { width: 44, height: 44, borderRadius: 22, backgroundColor: 'rgba(148,235,152,0.15)', alignItems: 'center', justifyContent: 'center' },
  clubResultInfo: { flex: 1 },
  clubResultName: { fontSize: 15, fontWeight: '600' },
  clubResultAddress: { fontSize: 13, marginTop: 2 },
});
